﻿
import Geometry from "ol/geom/Geometry";
import GeometryCollection from "ol/geom/GeometryCollection";
import Point from "ol/geom/Point";
import Polygon, { circular } from "ol/geom/Polygon";
import { ProjectionLike, transform } from "ol/proj";
import { Coordinate } from "ol/coordinate";

import { Gonio } from "../../utilities/Gonio";
import { Projections } from "../../models/Projections";

export const MIN_RADIUS = 0.1;

/**
 * Custom geometry for drawing a Geodesic circle
 * */
class GeodesicCircle extends GeometryCollection {

    public static isGeodesicCircle(geometry: Geometry | undefined) {

        if (geometry instanceof GeodesicCircle) {
            return geometry;
        }
        else if (geometry instanceof GeometryCollection) {
            const geometries = geometry.getGeometries();
            if (geometries.length == 2) {
                const point = geometries.filter(g => g instanceof Point).map(g => g as Point);
                const polygon = geometries.filter(g => g instanceof Polygon).map(g => g as Polygon);

                if (point.length && polygon.length)
                    return new GeodesicCircle(point[0], polygon[0]);
            }
        }
        return undefined
    }

    private point_: Point;
    public get point() {
        return this.point_;
    }

    private polygon_: Polygon;
    public get polygon() {
        return this.polygon_;
    }

    constructor(center: Coordinate, radius: number);
    constructor(point: Point, polygon: Polygon)
    constructor(point_center: Point | Coordinate, polygon_radius: Polygon | number) {
        super([]);

        if (!(point_center instanceof Point))
            point_center = new Point(point_center);
        this.point_ = point_center;

        if (polygon_radius instanceof Polygon)
            this.polygon_ = polygon_radius;
        else {
            this.polygon_ = new Polygon([]);
            this.setRadius(polygon_radius)
        }

        this.setGeometries([this.point_, this.polygon_])
    }

    public setGeometries(geometries: [Point, Polygon]) {
        this.point_ = geometries[0];
        this.polygon_ = geometries[1];
        super.setGeometries(geometries);
    }

    public setGeometriesArray(geometries: [Point, Polygon]) {
        this.point_ = geometries[0];
        this.polygon_ = geometries[1];
        super.setGeometriesArray(geometries);
    }

    public getCenter() {
        return this.point_.getCoordinates();
    }

    public setCenter(coordinate: Coordinate) {
        const current = this.point_.getCoordinates();
        const deltaX = coordinate[0] - current[0];
        const deltaY = coordinate[1] - current[1];
        this.point_.setCoordinates(coordinate);
        this.polygon_.translate(deltaX, deltaY);
    }

    public getRadius(projection: ProjectionLike = Projections.WebMercator) {
        const polygon = this.polygon_.getCoordinates()[0];
        const diameter = Gonio.distanceGC(polygon[0], polygon[(polygon.length - 1) / 2], projection);
        return Math.max(diameter / 2, MIN_RADIUS);
    }

    public setRadius(radius: number, projection: ProjectionLike = Projections.WebMercator) {
        const center = this.point_.getCoordinates();
        const circle = circular(
            transform(center, projection, Projections.WGS),
            Math.max(radius, MIN_RADIUS),
            128
        );

        circle.transform(Projections.WGS, projection);
        this.polygon_.setCoordinates(circle.getCoordinates())
    }

    public clone() {
        return new GeodesicCircle(this.point_.clone(), this.polygon_.clone());
    }
}


export default GeodesicCircle;